// Strategy Profile picker + read-only summary card.
// Used by Backtest, Walk-Forward, and Compare pages so historical replays
// always reuse the same composable strategy logic as the live engine.

import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/Bits";
import { api } from "@/lib/api";
import { isOppositeSignalExitEnabled } from "@/lib/exitRules";
import { countEffectiveConditions, summarizeGroups } from "@/lib/profileSummary";
import { AlertTriangle, FileText } from "lucide-react";

const COND_LABEL = {
  rsi_divergence: "RSI Div",
  rsi_absolute: "RSI Abs",
  cvd_divergence: "CVD Div",
  liquidation_filter: "Liq Filter",
};

const EXIT_LABEL = {
  manual: "Manual (no auto-close)",
  tp_sl: "TP / SL",
  time: "Time-based",
  opposite_signal: "Opposite signal",
  combined: "Combined",
};

export function ConditionDot({ type }) {
  const colors = {
    rsi_divergence: "#FFD23F",
    rsi_absolute: "#F59E0B",
    cvd_divergence: "#06B6D4",
    liquidation_filter: "#FF3B30",
  };
  return <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: colors[type] || "#71717A" }} />;
}

export function StrategyPicker({ value, onChange, disabled, testidPrefix = "strat-picker" }) {
  // value: { mode: "profile" | "active", profileId?: string }
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.strategyProfiles();
        const active = await api.strategyActive();
        if (!alive) return;
        setProfiles(list || []);
        setActiveId(active?.active_strategy_id || null);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  const selectedId = value?.profileId || activeId || "";

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Strategy profile</Label>
      <Select
        value={selectedId || ""}
        disabled={disabled}
        onValueChange={(v) => onChange?.({ mode: "profile", profileId: v })}
      >
        <SelectTrigger data-testid={`${testidPrefix}-select`} className="bg-[#1A1A22] border-border rounded-sm">
          <SelectValue placeholder="Select a strategy profile…" />
        </SelectTrigger>
        <SelectContent>
          {profiles.length === 0 ? (
            <SelectItem disabled value="__empty">No profiles configured. Open Strategies →</SelectItem>
          ) : (
            profiles.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}{p.id === activeId ? "  • ACTIVE" : ""}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <a href="/strategies" className="text-[10px] text-[#71717A] hover:text-white">Manage profiles →</a>
    </div>
  );
}

export function StrategySummary({ profile }) {
  if (!profile) {
    return (
      <div className="bg-[#0F0F14] border border-border rounded-sm p-4 text-xs text-[#71717A]">
        Select a strategy profile to see its conditions.
      </div>
    );
  }
  const conds = profile.conditions || [];
  const enabled = conds.filter((c) => c.enabled !== false);
  // Composite (Advanced Condition Groups) profiles store their
  // conditions inside `condition_groups[*].conditions` and leave
  // the flat `conditions` empty. Counting only the flat list made
  // every composite profile (e.g. "3 groups · 7 conditions") render
  // the misleading "No conditions enabled" warning here — exactly
  // the same legacy-flat-only display bug that affected the live
  // banner and SignalValidation header. The Backtest runtime path
  // (`services/backtest_engine.run_backtest` →
  // `services/strategy_profiles.evaluate_profile`) honours both
  // shapes; this is a UI-only counter fix. Shared with Layout and
  // SignalValidation via `lib/profileSummary`.
  const groups = summarizeGroups(profile);
  const totalEnabled = countEffectiveConditions(profile);
  const ex = profile.exit_rules || {};
  const exMode = ex.mode || "tp_sl";
  const isManual = exMode === "manual";

  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm p-4 space-y-3" data-testid="strat-summary">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={14} strokeWidth={1.5} className="text-[#A0A0AB]" />
          <div className="text-sm font-semibold">{profile.name}</div>
          <Badge kind="neutral">{profile.timeframe}</Badge>
          <Badge kind="neutral">cooldown {profile.cooldown_minutes}m</Badge>
        </div>
        <Badge kind={isManual ? "warn" : "on"}>{EXIT_LABEL[exMode] || exMode}</Badge>
      </div>

      {profile.description ? (
        <div className="text-xs text-[#A0A0AB]">{profile.description}</div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        {totalEnabled === 0 ? (
          <span className="text-[#FF3B30]" data-testid="strat-summary-empty">
            No conditions enabled — backtest will produce zero signals.
          </span>
        ) : groups.length > 0 ? (
          // Composite mode — surface group count + total effective
          // conditions instead of listing them inline (would be too
          // wide for 3+ groups with multiple conditions each).
          <>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#1A1A22] border border-border text-[#E4E4E7]"
              data-testid="strat-summary-grouped-count"
            >
              {groups.length} groups · {totalEnabled} conditions
            </span>
            <span className="text-[#71717A] font-mono text-[11px]">
              {groups.map((g, i) => (
                <span key={g.id || i}>
                  {i > 0 ? " · " : ""}
                  {g.label}: {g.count}
                </span>
              ))}
            </span>
          </>
        ) : (
          enabled.map((c, i) => (
            <span key={c.id || i} className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#1A1A22] border border-border">
              <ConditionDot type={c.type} />
              <span className="text-[#E4E4E7]">{COND_LABEL[c.type] || c.type}</span>
              {c.direction_filter && c.direction_filter !== "any" ? (
                <span className="ml-1 text-[#71717A]">({c.direction_filter})</span>
              ) : null}
              {i < enabled.length - 1 ? (
                <span className="ml-2 text-[#71717A]">{profile.combinator}</span>
              ) : null}
            </span>
          ))
        )}
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[#A0A0AB] font-mono">
        <span>Allow LONG: <span className={profile.allow_long ? "long-text" : "text-[#71717A]"}>{profile.allow_long ? "yes" : "no"}</span></span>
        <span>Allow SHORT: <span className={profile.allow_short ? "short-text" : "text-[#71717A]"}>{profile.allow_short ? "yes" : "no"}</span></span>
        {ex.take_profit_enabled ? <span>TP {Number(ex.take_profit_percent || 0).toFixed(2)}%</span> : null}
        {ex.stop_loss_enabled ? <span>SL {Number(ex.stop_loss_percent || 0).toFixed(2)}%</span> : null}
        {ex.max_duration_enabled && ex.max_duration_minutes ? <span>Max dur {ex.max_duration_minutes}m</span> : null}
        {isOppositeSignalExitEnabled(ex) ? <span>Close-on-opposite</span> : null}
      </div>

      {isManual ? (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] rounded-sm p-2.5 text-xs flex gap-2">
          <AlertTriangle size={14} strokeWidth={1.5} className="shrink-0 mt-px" />
          <span>
            <b>Manual exit mode</b> — backtest will not auto-close. Any positions still open at the end of the period are force-closed at the last close (counted as <code>forced_exits_at_end</code>).
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function DiagnosticsRow({ diagnostics }) {
  if (!diagnostics) return null;
  const cells = [
    { k: "signals_evaluated", label: "Evaluated" },
    { k: "signals_fired", label: "Fired" },
    { k: "rejected_by_conditions", label: "Rejected" },
    { k: "direction_filtered", label: "Direction filtered" },
    { k: "cooldown_blocks", label: "Cooldown blocks" },
    { k: "open_position_blocks", label: "Open-pos blocks" },
    { k: "forced_exits_at_end", label: "Forced exits @ end" },
  ];
  return (
    <div className="bg-[#0F0F14] border border-border rounded-sm p-3" data-testid="bt-diagnostics">
      <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Signal Diagnostics</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs font-mono">
        {cells.map((c) => (
          <div key={c.k} className="bg-[#1A1A22] border border-border rounded-sm px-2.5 py-1.5">
            <div className="text-[10px] text-[#71717A] uppercase tracking-wide">{c.label}</div>
            <div className="text-sm tabular-nums">{diagnostics[c.k] ?? 0}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
